import React, { useState, useEffect } from 'react';


import Image from '../../../components/AppImage';
import { Upload, Download, Trash2, FileText } from 'lucide-react';
import fileUploadService from '../../../services/fileUploadService';
import useRealtime from '../../../hooks/useRealtime';

const FileAttachmentsCard = ({ orderId }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { subscribeToOrderAttachments } = useRealtime();

  useEffect(() => {
    if (orderId) {
      fetchAttachments();
    }
  }, [orderId]);

  // Set up real-time subscription for attachments
  useEffect(() => {
    if (!orderId) return;

    const unsubscribe = subscribeToOrderAttachments(orderId, (payload) => {
      fetchAttachments(); // Refetch when changes occur
    });

    return unsubscribe;
  }, [orderId, subscribeToOrderAttachments]);

  const fetchAttachments = async () => {
    if (!orderId) return;

    try {
      setLoading(true);
      const { data, error } = await fileUploadService?.getOrderAttachments(orderId);
      
      if (error) {
        console.error('Error fetching attachments:', error);
        return;
      }

      setAttachments(data || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files?.length || !orderId) return;

    setUploading(true);
    try {
      const { data, errors } = await fileUploadService?.bulkUploadOrderAttachments(
        orderId, 
        Array.from(files)
      );

      if (data?.length > 0) {
        // Refresh attachments list
        await fetchAttachments();
      }

      if (errors?.length > 0) {
        console.error('Some files failed to upload:', errors);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileDelete = async (attachmentId) => {
    if (!window?.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      const { error } = await fileUploadService?.deleteAttachment(attachmentId);
      
      if (error) {
        console.error('Error deleting attachment:', error);
        return;
      }

      // Remove from local state
      setAttachments(prev => prev?.filter(att => att?.id !== attachmentId));
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const handleFileDownload = async (attachment) => {
    try {
      const { data, error } = await fileUploadService?.downloadAttachment(attachment?.id);
      
      if (error) {
        console.error('Error downloading file:', error);
        return;
      }

      // Create download link
      const url = URL.createObjectURL(data?.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = data?.filename;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    const files = e?.dataTransfer?.files;
    if (files) {
      handleFileUpload(files);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return 'Image';
    if (type?.includes('pdf')) return 'FileText';
    if (type?.includes('word')) return 'FileText';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return 'FileSpreadsheet';
    return 'File';
  };

  const getFileTypeColor = (type) => {
    if (type?.startsWith('image/')) return 'text-green-600';
    if (type?.includes('pdf')) return 'text-red-600';
    if (type?.includes('word')) return 'text-blue-600';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return 'text-emerald-600';
    return 'text-text-secondary';
  };

  const isImageFile = (type) => type?.startsWith('image/');

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">File Attachments</h3>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">File Attachments</h3>
        <span className="text-sm text-gray-500">
          {attachments?.length} file{attachments?.length !== 1 ? 's' : ''}
        </span>
      </div>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 mb-4 transition-colors ${
          dragActive 
            ? 'border-blue-400 bg-blue-50' :'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Uploading files...</p>
          </div>
        ) : (
          <div className="text-center">
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-2">
              Drag and drop files here, or{' '}
              <label className="text-blue-500 hover:text-blue-600 cursor-pointer">
                browse
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e?.target?.files)}
                />
              </label>
            </p>
            <p className="text-xs text-gray-500">
              Supports images, documents, PDFs, and more
            </p>
          </div>
        )}
      </div>
      {/* Attachments List */}
      <div className="space-y-3">
        {attachments?.map((attachment) => (
          <div key={attachment?.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
            <div className="flex items-center flex-1 min-w-0">
              {getFileIcon(attachment?.file_type)}
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {attachment?.file_name}
                </p>
                <div className="flex items-center text-xs text-gray-500">
                  <span>{formatFileSize(attachment?.file_size)}</span>
                  <span className="mx-2">•</span>
                  <span>{new Date(attachment?.created_at)?.toLocaleDateString()}</span>
                  <span className="mx-2">•</span>
                  <span>{attachment?.uploaded_by?.full_name}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFileDownload(attachment)}
                className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleFileDelete(attachment?.id)}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        
        {attachments?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No files attached yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileAttachmentsCard;